/**
 * Required Fields Configuration Constants
 *
 * This file contains field mappings and configurations for the Required Fields feature.
 * These mappings are used across different modules (Clients, Drivers, Locations, Providers, Trips, Vehicles)
 * to consistently handle field validation and display.
 *
 * Data extracted from:
 * - Robot Framework test files (*.robot)
 * - YAML configuration files (*.en.yml)
 *
 * Values are i18n translation keys resolved via t() in the UI components.
 */

export type CategoryType = 'customer' | 'driver' | 'location' | 'company' | 'trip' | 'vehicle';

export interface FieldMapping {
  [key: string]: string;
}

export interface CategoryFieldMapping {
  customer: FieldMapping;
  driver: FieldMapping;
  location: FieldMapping;
  company: FieldMapping;
  trip: FieldMapping;
  vehicle: FieldMapping;
}

/**
 * Field mappings extracted from YML configuration files
 * Maps backend field keys to i18n translation keys
 */
export const FIELD_MAPPINGS: CategoryFieldMapping = {
  customer: {
    // Mandatory fields
    first_name: 'system.requiredFields.fields.firstName',
    last_name: 'system.requiredFields.fields.lastName',
    vehicle_req: 'system.requiredFields.fields.vehicleRequirement',
    language_code: 'system.requiredFields.fields.language',

    // Optional fields
    assistant: 'system.requiredFields.fields.personalCareAssistant',
    combined_weight: 'system.requiredFields.fields.combinedWeight',
    comment: 'system.requiredFields.fields.note',
    customer_number: 'system.requiredFields.fields.clientNumber',
    date_of_birth: 'system.requiredFields.fields.dateOfBirth',
    default_device: 'system.requiredFields.fields.defaultDevice',
    default_provider: 'system.requiredFields.fields.defaultProvider',
    disability_mental: 'system.requiredFields.fields.disabilityMental',
    disability_physical: 'system.requiredFields.fields.disabilityPhysical',
    disability_visual: 'system.requiredFields.fields.disabilityVisual',
    extra1: 'system.requiredFields.fields.disabled',
    driver_note: 'system.requiredFields.fields.driverNote',
    extra2: 'system.requiredFields.fields.elderly',
    dropoff_delay: 'system.requiredFields.fields.additionalUnloadingTime',
    email: 'system.requiredFields.fields.email',
    emergency_address: 'system.requiredFields.fields.emergencyAddress',
    emergency_contact: 'system.requiredFields.fields.emergencyContact',
    emergency_phone_day: 'system.requiredFields.fields.emergencyPhoneDay',
    emergency_phone_night: 'system.requiredFields.fields.emergencyPhoneNight',
    enabled: 'system.requiredFields.fields.status',
    ethnicity: 'system.requiredFields.fields.ethnicity',
    gender: 'system.requiredFields.fields.gender',
    home_city: 'system.requiredFields.fields.city',
    home_county: 'system.requiredFields.fields.county',
    home_postcode: 'system.requiredFields.fields.zipCode',
    home_state: 'system.requiredFields.fields.state',
    home_street: 'system.requiredFields.fields.street',
    home_street_number: 'system.requiredFields.fields.streetNumber',
    home_street_suffix: 'system.requiredFields.fields.apartment',
    household_ages: 'system.requiredFields.fields.householdAges',
    household_members: 'system.requiredFields.fields.householdMembers',
    income_level: 'system.requiredFields.fields.incomeLevel',
    ivr_callout_allowed: 'system.requiredFields.fields.sendIvrNightBefore',
    ivr_notification_allowed: 'system.requiredFields.fields.sendIvrOnTheWay',
    extra3: 'system.requiredFields.fields.livingWithin',
    marital_status: 'system.requiredFields.fields.maritalStatus',
    medicaid_number: 'system.requiredFields.fields.medicaid',
    medicare_number: 'system.requiredFields.fields.medicare',
    middle_name_initials: 'system.requiredFields.fields.middleInitial',
    no_public_transport: 'system.requiredFields.fields.allowPublicTransportation',
    pca_details: 'system.requiredFields.fields.pcaDetails',
    phone_landline: 'system.requiredFields.fields.landlinePhone',
    phone_mobile: 'system.requiredFields.fields.mobilePhone',
    phone_sms_allowed: 'system.requiredFields.fields.sendSmsOnTheWay',
    phone_ttd: 'system.requiredFields.fields.phoneTtd',
    physician_name: 'system.requiredFields.fields.physicianName',
    physician_phone: 'system.requiredFields.fields.physicianPhone',
    pickup_delay: 'system.requiredFields.fields.additionalLoadingTime',
    reservation_note: 'system.requiredFields.fields.reservationNote',
    ssn_encrypted: 'system.requiredFields.fields.socialSecurityNumber',
    title: 'system.requiredFields.fields.title',
  },

  driver: {
    // Mandatory fields
    first_name: 'system.requiredFields.fields.firstName',
    last_name: 'system.requiredFields.fields.lastName',
    login: 'system.requiredFields.fields.mdtUsername',
    company: 'system.requiredFields.fields.provider',
    skill: 'system.requiredFields.fields.skillLevel',

    // Optional fields
    city: 'system.requiredFields.fields.city',
    comment: 'system.requiredFields.fields.note',
    date_of_birth: 'system.requiredFields.fields.dateOfBirth',
    driver_license_expiration_date: 'system.requiredFields.fields.driverLicenseExpirationDate',
    driver_license_number: 'system.requiredFields.fields.driverLicenseNumber',
    driver_license_state: 'system.requiredFields.fields.driverLicenseIssuingState',
    emergency_comment: 'system.requiredFields.fields.emergencyContactNote',
    emergency_contact: 'system.requiredFields.fields.emergencyContactName',
    emergency_phone: 'system.requiredFields.fields.emergencyContactPhone',
    emergency_phone_ext: 'system.requiredFields.fields.emergencyContactPhoneExtension',
    emergency_relation: 'system.requiredFields.fields.emergencyContactRelation',
    employee_id: 'system.requiredFields.fields.employeeNumber',
    expiration_date: 'system.requiredFields.fields.terminationDate',
    hire_date: 'system.requiredFields.fields.hireDate',
    middle_initial: 'system.requiredFields.fields.middleInitial',
    phone: 'system.requiredFields.fields.phone',
    phone_ext: 'system.requiredFields.fields.phoneExtension',
    postcode: 'system.requiredFields.fields.zipCode',
    seniority: 'system.requiredFields.fields.seniority',
    ssn_last4: 'system.requiredFields.fields.ssnLast4',
    state: 'system.requiredFields.fields.state',
    street: 'system.requiredFields.fields.street',
    street_number: 'system.requiredFields.fields.streetNumber',
  },

  location: {
    // Mandatory fields
    type: 'system.requiredFields.fields.type',

    // Optional fields
    city: 'system.requiredFields.fields.city',
    county: 'system.requiredFields.fields.county',
    lat: 'system.requiredFields.fields.latitude',
    location_code: 'system.requiredFields.fields.code',
    lon: 'system.requiredFields.fields.longitude',
    name: 'system.requiredFields.fields.name',
    number: 'system.requiredFields.fields.number',
    phone: 'system.requiredFields.fields.phone',
    postcode: 'system.requiredFields.fields.zipCode',
    state: 'system.requiredFields.fields.state',
    street: 'system.requiredFields.fields.street',
  },

  company: {
    // Mandatory fields
    name: 'system.requiredFields.fields.name',
    pt_priority: 'system.requiredFields.fields.priority',

    // Optional fields
    city: 'system.requiredFields.fields.city',
    code: 'system.requiredFields.fields.otherProviderId',
    contact_email: 'system.requiredFields.fields.email',
    contact_name: 'system.requiredFields.fields.contactName',
    contact_phone1: 'system.requiredFields.fields.contactPhone1',
    contact_phone2: 'system.requiredFields.fields.contactPhone2',
    credentialing_id: 'system.requiredFields.fields.credentialingId',
    eligibility_end: 'system.requiredFields.fields.eligibilityEnd',
    eligibility_start: 'system.requiredFields.fields.eligibilityStart',
    exempted_from_location_codes: 'system.requiredFields.fields.exemptedFromLocationCodes',
    external_provider_type: 'system.requiredFields.fields.externalProviderType',
    postcode: 'system.requiredFields.fields.zipCode',
    provider_type: 'system.requiredFields.fields.type',
    street_address: 'system.requiredFields.fields.street',
    transportation_subcontractor: 'system.requiredFields.fields.subcontractor',
    edi_billing_provider_tax_identifier: 'system.requiredFields.fields.taxId',
  },

  trip: {
    // Mandatory fields
    customer_fare_type: 'system.requiredFields.fields.fareType',
    tf_decision: 'system.requiredFields.fields.fundingSource',
    tf_dropoff_name: 'system.requiredFields.fields.dropOffStreet',
    tf_pickup_name: 'system.requiredFields.fields.pickUpStreet',
    tf_purpose: 'system.requiredFields.fields.purpose',
    tf_requested_date: 'system.requiredFields.fields.tripDate',
    tf_requested_time: 'system.requiredFields.fields.requestedTime',
    tf_sponsor: 'system.requiredFields.fields.sponsor',
    tf_target_runs: 'system.requiredFields.fields.targetRuns',

    // Optional fields
    additional_fare_type: 'system.requiredFields.fields.additionalFareType',
    assistant: 'system.requiredFields.fields.personalCareAssistant',
    billing_code_set: 'system.requiredFields.fields.billingCodeSet',
    children: 'system.requiredFields.fields.children',
    companions: 'system.requiredFields.fields.companions',
    devices: 'system.requiredFields.fields.device',
    dropoff_delay: 'system.requiredFields.fields.additionalUnloadingTime',
    dropoff_phone: 'system.requiredFields.fields.dropOffPhone',
    external_trip_id: 'system.requiredFields.fields.externalId',
    flexibility_minus: 'system.requiredFields.fields.negotiationWindowBefore',
    flexibility_plus: 'system.requiredFields.fields.negotiationWindowAfter',
    ivr_callout_allowed: 'system.requiredFields.fields.sendIvrNightBefore',
    ivr_notification_allowed: 'system.requiredFields.fields.sendIvrOnTheWay',
    other_passengers: 'system.requiredFields.fields.otherPassengers',
    passenger_dropoff_note: 'system.requiredFields.fields.dropOffNote',
    passenger_phone: 'system.requiredFields.fields.clientPhone',
    passenger_pickup_note: 'system.requiredFields.fields.pickUpNote',
    pickup_delay: 'system.requiredFields.fields.additionalLoadingTime',
    pickup_phone: 'system.requiredFields.fields.pickUpPhone',
    sms_permission: 'system.requiredFields.fields.sendSmsOnTheWay',
    target_runs: 'system.requiredFields.fields.targetRunUse',
    target_surcharges: 'system.requiredFields.fields.surcharges',
    time_type: 'system.requiredFields.fields.timingType',
    transportation_provider: 'system.requiredFields.fields.transportationProvider',
    travel_alone: 'system.requiredFields.fields.privateTrip',
    trip_group: 'system.requiredFields.fields.tripGroup',
    vehicle_req: 'system.requiredFields.fields.vehicleRequirement',
  },

  vehicle: {
    // Mandatory fields
    delivery_system: 'system.requiredFields.fields.dispatchType',
    public_id: 'system.requiredFields.fields.name',
    company: 'system.requiredFields.fields.provider',
    vehicle_type: 'system.requiredFields.fields.vehicleType',

    // Optional fields
    capacity: 'system.requiredFields.fields.seatsMax',
    current_driver: 'system.requiredFields.fields.driver',
    enabled: 'system.requiredFields.fields.enabled',
    flip_capacity: 'system.requiredFields.fields.flipSeats',
    flip_wheelchair_capacity: 'system.requiredFields.fields.flipSeatWheelchairPlaces',
    imei: 'system.requiredFields.fields.mdtImeiCode',
    license: 'system.requiredFields.fields.license',
    license_state: 'system.requiredFields.fields.licenseState',
    make: 'system.requiredFields.fields.make',
    model: 'system.requiredFields.fields.model',
    odometer: 'system.requiredFields.fields.odometer',
    password: 'system.requiredFields.fields.mdtPassword',
    phone: 'system.requiredFields.fields.phone',
    stretcher_capacity: 'system.requiredFields.fields.stretcherPlaces',
    use_wc2: 'system.requiredFields.fields.largeWheelchairCapacity',
    vin: 'system.requiredFields.fields.vin',
    wc2_to_wc: 'system.requiredFields.fields.largeWheelchairRatio',
    wheelchair_capacity: 'system.requiredFields.fields.wheelchairPlacesMax',
    year: 'system.requiredFields.fields.year',
  },
};

/**
 * Default mandatory fields for each category
 * These fields are always required and cannot be made optional
 * Based on backend validation rules
 */
export const MANDATORY_FIELDS: Record<CategoryType, string[]> = {
  customer: ['first_name', 'last_name', 'vehicle_req', 'language_code'],
  driver: ['first_name', 'last_name', 'login', 'company', 'skill'],
  location: ['type'],
  company: ['name', 'pt_priority'],
  trip: [
    'customer_fare_type',
    'tf_decision',
    'tf_dropoff_name',
    'tf_pickup_name',
    'tf_purpose',
    'tf_requested_date',
    'tf_requested_time',
    'tf_sponsor',
    'tf_target_runs',
  ],
  vehicle: ['delivery_system', 'public_id', 'company', 'vehicle_type'],
};

/**
 * Get all optional field keys for a category (excluding mandatory fields)
 * @param category - The category type (customer, driver, location, company, trip, vehicle)
 * @returns Array of optional field keys
 */
export const getOptionalFieldKeys = (category: CategoryType): string[] => {
  const allFields = Object.keys(FIELD_MAPPINGS[category]);
  const mandatory = MANDATORY_FIELDS[category] || [];
  return allFields.filter((key) => !mandatory.includes(key));
};

/**
 * Get the i18n translation key for a field
 * @param category - The category type
 * @param fieldKey - The backend field key
 * @returns The i18n translation key (resolve with t() in UI components)
 */
export const getFieldLabel = (category: CategoryType, fieldKey: string): string => {
  return FIELD_MAPPINGS[category][fieldKey] || fieldKey;
};

/**
 * Get all mandatory fields for a category with their translation keys
 * @param category - The category type
 * @returns Array of objects with key and label (i18n key)
 */
export const getMandatoryFieldsWithLabels = (category: CategoryType): { key: string; label: string }[] => {
  return MANDATORY_FIELDS[category].map((key) => ({
    key,
    label: getFieldLabel(category, key),
  }));
};

/**
 * Get all optional fields for a category with their translation keys
 * @param category - The category type
 * @returns Array of objects with key and label (i18n key)
 */
export const getOptionalFieldsWithLabels = (category: CategoryType): { key: string; label: string }[] => {
  return getOptionalFieldKeys(category).map((key) => ({
    key,
    label: getFieldLabel(category, key),
  }));
};

/**
 * Check if a field is mandatory for a category
 * @param category - The category type
 * @param fieldKey - The field key to check
 * @returns True if the field is mandatory
 */
export const isFieldMandatory = (category: CategoryType, fieldKey: string): boolean => {
  return MANDATORY_FIELDS[category]?.includes(fieldKey) || false;
};

/**
 * Map frontend category names to backend category names
 */
export const CATEGORY_MAP: Record<string, CategoryType> = {
  clients: 'customer',
  drivers: 'driver',
  locations: 'location',
  providers: 'company',
  trips: 'trip',
  vehicles: 'vehicle',
};

/**
 * Reverse map: backend to frontend category names
 */
export const REVERSE_CATEGORY_MAP: Record<CategoryType, string> = {
  customer: 'clients',
  driver: 'drivers',
  location: 'locations',
  company: 'providers',
  trip: 'trips',
  vehicle: 'vehicles',
};
